import { Button, Tooltip } from '@mui/material';
import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import classSet from 'react-classset';
import { Link } from 'react-router-dom';

import alertIcon from '@/assets/icons/vn-icon-alert.svg';
import { useModalContext } from '@/modals/VNModalProvider';
import itemUtils from '@/utils/itemUtils';

import { Permission } from '../../enums';
import type { Item } from '../../models';
import type { Permissions } from '../../models/Permissions';

export interface Iprops {
  item: Item;
  permissions?: Permissions;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => void;
  href?: string;
  hideStatus?: boolean;
}

const FuelAssetThumbnail = ({ item, permissions, onClick, hideStatus = false, href }: Iprops) => {
  const modal = useModalContext();
  const Component = useMemo(() => (href ? Link : 'div'), [href]);

  const hasPermission = useMemo(() => !permissions || permissions.has(Permission.EditAsset), [permissions]);

  const classes = useMemo(
    () =>
      classSet({
        'fuel-asset-thumbnail': true,
        'collection-thumbnail': ['ClipGroup', 'NestedChannel'].includes(item?.source),
        'nested-channel-clip': item?.source === 'NestedChannel',
        'live-event-clip': item?.source === 'LiveEvent',
        'no-clickable': !hasPermission, // blocks click on warnings too
        'no-pointer': !onClick && !href,
        [`thumbnail-border-${item?.clipStatusMarker}`]: !hideStatus && item?.clipStatusMarker !== 'none',
      }),
    [item?.source, item?.clipStatusMarker, hideStatus, hasPermission, onClick, href]
  );

  const onClickIngestWarnings = (e: MouseEvent<HTMLButtonElement>, asset: Item) => {
    e.stopPropagation();

    switch (asset?.ingestWarnings[0]) {
      case 'no-audio':
        modal.onOpen(
          'Missing Audio Track',
          'xl',
          <div className="fuel-asset-thumbnail-warning-modal">
            <div className="warning-modal-inner">
              <p>
                This video file does not have an audio track. It will not playout in the stream it is associated with if
                published. Please remove it and upload a corrected version. (Note: the file does not have to have audio, but it
                needs at least one audio track)
              </p>
            </div>
          </div>,
          asset.id
        );
        break;
      default:
    }
  };

  const setDefaultImg = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = '/static/images/thumbnail.png';
  };

  return (
    <Component
      role="link"
      tabIndex={0}
      className={classes}
      title={item?.differenceVariantCodesWithChannel ? `Missing variants: ${item?.differenceVariantCodesWithChannel}` : ''}
      onClick={(event) => onClick?.(event)}
      to={href}
    >
      <div className="thumbnail-container">
        {item?.ingestWarnings?.length > 0 && (
          <Tooltip title="click to view details" placement="top">
            <Button className="ingest-warning" onClick={(e) => onClickIngestWarnings(e, item)}>
              {/* <FontAwesomeIcon icon={faTriangleExclamation} /> */}
              <img src={alertIcon} alt="warning-alert" />
            </Button>
          </Tooltip>
        )}

        <img
          className="group-thumbnail"
          src={item?.thumbnailSrc || '/static/images/thumbnail.png'}
          loading="lazy"
          alt=""
          onError={setDefaultImg}
        />
        <img
          className="thumbnail"
          src={item?.thumbnailSrc || '/static/images/thumbnail.png'}
          loading="lazy"
          alt=""
          onError={setDefaultImg}
        />

        {item && <img className="clip-group-icon" src={itemUtils.getClipIcon(item)} alt="" />}
      </div>
    </Component>
  );
};

export default FuelAssetThumbnail;
